import React from "react";
import { ReportStyles } from "./Styles/ReportStyles";
import { useMeasure } from "react-use";

import {
  getLastFullQuarter,
  FORMAT_READABLE_DATE,
} from "reports/utils/timeUtils";
import QuarterlyBarChart from "../qiagen/ReportContent/Charts/QuarterlyReportBarChart";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import {
  dataDumpGetterQI,
  getQuarterTimeSpan,
  splitUpTimespan,
  allDataLoaded,
  separateByShift,
  QiagenQuarterlyDataType,
  QiagenDataByMachineType,
} from "../qiagen/utils/QiagenQuarterlyUtils";
import { DataDumpType, ReportParams } from "../../../types";
import { getDateStringWithGeAndLt } from "../../../components/Reports/ReportHeader";
import QuarterlyOverviewSheet from "./ReportContent/Sheets/QuarterlyOverviewSheet";
import { getMidgardDataDumps } from "../../../pages/utils";
import { useParams } from "react-router";

const machemetryIds = [
  "qi_d_01_2_0", // SCK
  "qi_d_01_1_0", // Tube Filler
  "qi_d_01_3_0", // Bottle Filler
  "qi_d_01_4_0", // QFT Side Bagger./src/pages/ReportsPage/utils.ts
  "qi_d_01_5_0", // Mactec Poucher
  "qi_d_01_6_0", // Vial filler
  "qi_d_01_7_0", // Crimper and Vial Labeler
  "qi_d_01_8_0", // Small Volume Filler
  "qi_d_01_9_0", // Spin Column System
  "qi_d_01_10_0", // Sortimat
  "qi_d_01_11_0", // MB25 Filler/Capper
  "qi_d_01_12_0", // EZ1 System
];

const QI_QUARTERLY_CHART_WIDTH = 1450;
const ELEMENTS_ON_SECOND_PAGE = 8;

export const FinalChart = ({
  width,
  dateString,
  subHeader,
  dataBySku,
  dataByMachine,
  title,
}: {
  width: number;
  dateString: string;
  subHeader: string;
  dataBySku?: QiagenQuarterlyDataType[];
  dataByMachine?: QiagenDataByMachineType;
  title: string;
}) => (
  <>
    <QuarterlyBarChart
      headerTitle="Quarterly REPORT OVERVIEW"
      dateString={dateString}
      subHeader={subHeader}
      data={dataBySku?.slice(0, dataBySku.length - ELEMENTS_ON_SECOND_PAGE)} // will be fixed up to be dynamic instead of static in the future
      width={width}
      title={title}
    />
    <QuarterlyBarChart
      headerTitle="Quarterly REPORT OVERVIEW"
      dateString={dateString}
      subHeader={subHeader}
      data={dataBySku?.slice(
        dataBySku.length - ELEMENTS_ON_SECOND_PAGE, // the quarterly charts are statically created to fit two pages, will adapt for multi page later
        dataBySku.length
      )}
      width={width}
      title={title}
    />
    <QuarterlyOverviewSheet
      headerTitle="Quarterly REPORT OVERVIEW"
      dateString={dateString}
      subHeader={subHeader}
      data={dataByMachine}
      width={width}
      title={title}
    />
  </>
);

export default function Quarterly() {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const { qString, year } = getLastFullQuarter();
  const { quarterGe, quarterLt } = getQuarterTimeSpan(year, qString);
  const ranges = splitUpTimespan(quarterGe, quarterLt); // we cannot query for a full quarter, we will combine the data later

  const { ckey: tenant } = useParams() as ReportParams;
  const subHeader = `${tenant.toUpperCase()} - Quarterly - ${qString}`;
  const dateString = getDateStringWithGeAndLt(
    Number(quarterGe),
    Number(quarterLt),
    FORMAT_READABLE_DATE
  );

  const dataDumpsArray = getMidgardDataDumps({
    tenant,
    machemetryIds,
    ranges,
  });

  const combinedTotalQuarterlyData =
    Array.isArray(dataDumpsArray) && allDataLoaded(dataDumpsArray)
      ? dataDumpsArray?.reduce((acc, { data }) => {
          return data?.dataDump ? [...acc, ...data.dataDump] : [...acc];
        }, [] as DataDumpType[])
      : null;

  const {
    shiftOne,
    shiftTwo,
    shiftThree,
  }: {
    shiftOne: DataDumpType[] | null;
    shiftTwo: DataDumpType[] | null;
    shiftThree: DataDumpType[] | null;
  } =
    allDataLoaded(dataDumpsArray) && combinedTotalQuarterlyData
      ? separateByShift(combinedTotalQuarterlyData)
      : { shiftOne: null, shiftTwo: null, shiftThree: null };

  const shiftOneData = shiftOne ? dataDumpGetterQI(shiftOne) : null;
  const shiftTwoData = shiftTwo ? dataDumpGetterQI(shiftTwo) : null;
  const shiftThreeData = shiftThree ? dataDumpGetterQI(shiftThree) : null;
  const combinedData = [
    { ...shiftOneData, title: "Quarterly Data Shift 1" },
    { ...shiftTwoData, title: "Quarterly Data Shift 2" },
    { ...shiftThreeData, title: "Quarterly Data Shift 3" },
  ];

  document.title = `${subHeader} - ${year}`;

  return !allDataLoaded(dataDumpsArray) ? (
    <LoadingCircle />
  ) : (
    <ReportStyles>
      {combinedData.map(shiftData => {
        return (
          <React.Fragment key={shiftData.title}>
            <div ref={ref} className="reportContent">
              <FinalChart
                dateString={dateString}
                subHeader={subHeader}
                {...shiftData}
                width={width}
              />
            </div>
            <div className="reportPrintContent">
              <FinalChart
                dateString={dateString}
                subHeader={subHeader}
                width={QI_QUARTERLY_CHART_WIDTH}
                {...shiftData}
                key={shiftData.title}
              />
            </div>
          </React.Fragment>
        );
      })}
    </ReportStyles>
  );
}
